import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { throwFromResponse } from '../lib/apiErrorMessage';
import type {
  DepositAdjustPayload,
  DepositBalance,
  DepositLedger,
  DepositWithdrawal,
  DepositWithdrawalStatus,
  MileagePagination,
  PagedResult,
} from '../types';

const BASE = '/api/admin/v1/deposits';

interface BalanceEnvelope {
  success: boolean;
  data?: DepositBalance | null;
}

interface PageEnvelope<T> {
  success: boolean;
  data?: { content?: T[] } | null;
  meta?: { pagination?: { page: number; size: number; totalItems: number; totalPages: number } } | null;
}

interface WithdrawalEnvelope {
  success: boolean;
  data?: DepositWithdrawal | null;
}

export interface DepositLedgerQuery {
  userId?: string | undefined;
  kind?: string | undefined;
  occurredFrom?: string | undefined;
  occurredTo?: string | undefined;
  /** UI 1-based → BE 0-based 변환. */
  page: number;
  size: number;
}

export interface DepositWithdrawalQuery {
  status?: DepositWithdrawalStatus | undefined;
  page: number;
  size: number;
}

export const depositBalanceQueryKey = (userId: string) =>
  ['admin', 'deposit', 'balance', userId] as const;

export const depositLedgerQueryKey = (q: DepositLedgerQuery) =>
  [
    'admin',
    'deposit',
    'ledger',
    q.userId ?? '',
    q.kind ?? '',
    q.occurredFrom ?? '',
    q.occurredTo ?? '',
    q.page,
    q.size,
  ] as const;

export const depositWithdrawalsQueryKey = (q: DepositWithdrawalQuery) =>
  ['admin', 'deposit', 'withdrawals', q.status ?? '', q.page, q.size] as const;

const toPagination = (
  meta: PageEnvelope<unknown>['meta'],
  fallback: { page: number; size: number; count: number },
): MileagePagination => {
  const p = meta?.pagination;
  if (!p) {
    return {
      page: fallback.page,
      size: fallback.size,
      totalItems: fallback.count,
      totalPages: 1,
    };
  }
  return { page: p.page + 1, size: p.size, totalItems: p.totalItems, totalPages: p.totalPages };
};

const fetchBalance = async (userId: string): Promise<DepositBalance> => {
  const res = await adminFetch(`${BASE}/users/${userId}/balance`);
  if (!res.ok) await throwFromResponse(res, '예치금 잔액을 불러오지 못했습니다.');
  const env = (await res.json()) as BalanceEnvelope;
  return env.data ?? { userId, balance: 0 };
};

const fetchLedger = async (q: DepositLedgerQuery): Promise<PagedResult<DepositLedger>> => {
  const params = new URLSearchParams({
    page: String(Math.max(0, q.page - 1)),
    size: String(q.size),
  });
  if (q.userId) params.set('userId', q.userId);
  if (q.kind) params.set('kind', q.kind);
  if (q.occurredFrom) params.set('occurredFrom', q.occurredFrom);
  if (q.occurredTo) params.set('occurredTo', q.occurredTo);
  const res = await adminFetch(`${BASE}/ledger?${params.toString()}`);
  if (!res.ok) await throwFromResponse(res, '예치금 원장을 불러오지 못했습니다.');
  const env = (await res.json()) as PageEnvelope<DepositLedger>;
  const items = env.data?.content ?? [];
  return { items, pagination: toPagination(env.meta, { page: q.page, size: q.size, count: items.length }) };
};

const fetchWithdrawals = async (
  q: DepositWithdrawalQuery,
): Promise<PagedResult<DepositWithdrawal>> => {
  const params = new URLSearchParams({
    page: String(Math.max(0, q.page - 1)),
    size: String(q.size),
  });
  if (q.status) params.set('status', q.status);
  const res = await adminFetch(`${BASE}/withdrawals?${params.toString()}`);
  if (!res.ok) await throwFromResponse(res, '예치금 출금 목록을 불러오지 못했습니다.');
  const env = (await res.json()) as PageEnvelope<DepositWithdrawal>;
  const items = env.data?.content ?? [];
  return { items, pagination: toPagination(env.meta, { page: q.page, size: q.size, count: items.length }) };
};

export const useDepositBalanceQuery = (userId: string | null) =>
  useQuery<DepositBalance>({
    queryKey: depositBalanceQueryKey(userId ?? ''),
    queryFn: () => fetchBalance(userId as string),
    enabled: Boolean(userId),
  });

export const useDepositLedgerQuery = (q: DepositLedgerQuery, enabled = true) =>
  useQuery<PagedResult<DepositLedger>>({
    queryKey: depositLedgerQueryKey(q),
    queryFn: () => fetchLedger(q),
    enabled,
  });

export const useDepositWithdrawalsQuery = (q: DepositWithdrawalQuery) =>
  useQuery<PagedResult<DepositWithdrawal>>({
    queryKey: depositWithdrawalsQueryKey(q),
    queryFn: () => fetchWithdrawals(q),
  });

export const useAdminDepositMutation = () => {
  const qc = useQueryClient();
  const invalidate = (userId?: string) => {
    void qc.invalidateQueries({ queryKey: ['admin', 'deposit'] });
    if (userId) void qc.invalidateQueries({ queryKey: depositBalanceQueryKey(userId) });
  };

  const postAdjust = async (path: string, body: DepositAdjustPayload): Promise<Response> => {
    const headers = { 'Idempotency-Key': idempotencyKeyFor('POST', path, body) };
    return adminFetch(path, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  };

  const grant = useMutation({
    mutationFn: async (payload: DepositAdjustPayload): Promise<void> => {
      const res = await postAdjust(`${BASE}/grant`, payload);
      if (!res.ok) await throwFromResponse(res, '예치금 지급에 실패했습니다.');
    },
    onSuccess: (_d, payload) => invalidate(payload.userId),
  });

  const deduct = useMutation({
    mutationFn: async (payload: DepositAdjustPayload): Promise<void> => {
      const res = await postAdjust(`${BASE}/deduct`, payload);
      if (!res.ok) await throwFromResponse(res, '예치금 차감에 실패했습니다.');
    },
    onSuccess: (_d, payload) => invalidate(payload.userId),
  });

  const postWithdrawal = async (
    requestId: string,
    action: 'approve' | 'pay' | 'reject',
    body?: { reason: string },
  ): Promise<DepositWithdrawal> => {
    const path = `${BASE}/withdrawals/${requestId}/${action}`;
    const headers = { 'Idempotency-Key': idempotencyKeyFor('POST', path, body ?? {}) };
    const res = await adminFetch(path, {
      method: 'POST',
      headers,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) await throwFromResponse(res, '예치금 출금 처리에 실패했습니다.');
    const env = (await res.json()) as WithdrawalEnvelope;
    if (!env.data) throw new Error('출금 처리 응답이 비어 있습니다.');
    return env.data;
  };

  const approveWithdrawal = useMutation({
    mutationFn: (requestId: string) => postWithdrawal(requestId, 'approve'),
    onSuccess: (w) => invalidate(w.userId),
  });

  const payWithdrawal = useMutation({
    mutationFn: (requestId: string) => postWithdrawal(requestId, 'pay'),
    onSuccess: (w) => invalidate(w.userId),
  });

  const rejectWithdrawal = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason: string }) =>
      postWithdrawal(requestId, 'reject', { reason }),
    onSuccess: (w) => invalidate(w.userId),
  });

  return { grant, deduct, approveWithdrawal, payWithdrawal, rejectWithdrawal };
};
